import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import {
  clearTokens,
  getAccessToken,
  getActiveTenantId,
  getRefreshToken,
  setActiveTenant,
  setTokens,
} from '@/auth/store';
import { showToastImperative } from '@/components/ToastProvider';

export const api = axios.create({
  baseURL: '/api/v1',
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Порція 3.0.5.1b: автоматично передаємо tenant context у всі запити.
  // Глобальні endpoint'и (login, signup, my-tenants, create-tenant,
  // invitations/info, invitations/accept) самі ігнорують header — це робить
  // backend, не фронт.
  const tenantId = getActiveTenantId();
  if (tenantId) {
    config.headers['X-Tenant-Id'] = tenantId;
  }
  return config;
});

// Авто-refresh при 401: пытаемся обновить access через refresh-токен.
// Если refresh не сработал — чистим стор и редиректим на /login.
let refreshPromise: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  const refresh = getRefreshToken();
  if (!refresh) return null;
  try {
    const resp = await axios.post('/api/v1/auth/refresh', { refresh_token: refresh });
    const { access_token, refresh_token } = resp.data;
    setTokens(access_token, refresh_token);
    return access_token;
  } catch {
    clearTokens();
    return null;
  }
}

/**
 * Перевірка чи відповідь — це випадок «суспендований/видалений tenant'ний
 * доступ». Backend (deps.get_tenant_context) повертає 403 з detail
 * "No active membership in this tenant" коли user має валідний access_token,
 * але його membership у X-Tenant-Id вже не ACTIVE.
 *
 * Поведінка фронта (за спецою 3.0.5 розд. 2.4):
 * 1. Скидаємо active_tenant_id (бо він тепер невалідний).
 * 2. Чистимо React Query cache (зробить вищий рівень — тут не маємо доступу
 *    до queryClient'а; передадимо подію через `notifyTenantContextLost`).
 * 3. Redirect на /select-tenant з повідомленням.
 */
function isMembershipLostError(error: AxiosError): boolean {
  if (error.response?.status !== 403) return false;
  const detail = (error.response.data as { detail?: string } | undefined)?.detail;
  return typeof detail === 'string' && detail.toLowerCase().includes('membership');
}

/**
 * Перевірка чи 403 — це «недостатньо прав» від permission-enforcer'а
 * (Порція 3.0.7). Backend повертає detail вигляду:
 *   «Недостатньо прав. Потрібно: sales.realization.create»
 *
 * Поведінка фронта: показуємо alert з зрозумілим повідомленням. Це
 * додає UX-feedback навіть якщо кнопка не обгорнута <RequirePermission>
 * (для випадків коли user якось дійшов до неї). Sidebar + AutoRouteGuard
 * уже ховають доступ до сторінок, тому 403 «нема прав» — рідкісний кейс.
 */
function isInsufficientPermissionError(error: AxiosError): boolean {
  if (error.response?.status !== 403) return false;
  const detail = (error.response.data as { detail?: string } | undefined)?.detail;
  return typeof detail === 'string' && detail.startsWith('Недостатньо прав');
}

/**
 * Деякі endpoint'и обробляють 422 локально (показують inline у формі)
 * і НЕ хочуть глобального toast. Маркер — config.headers['X-Silent-422'].
 * За замовчуванням toast показується для всіх 422.
 */
function isMutationSilenced(error: AxiosError): boolean {
  const config = error.config;
  if (!config?.headers) return false;
  return Boolean(
    (config.headers as Record<string, unknown>)['X-Silent-422']
  );
}

api.interceptors.response.use(
  (r) => r,
  async (error: AxiosError) => {
    const original = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // 401 — refresh JWT.
    if (
      error.response?.status === 401 &&
      original &&
      !original._retry &&
      !original.url?.includes('/auth/login') &&
      !original.url?.includes('/auth/refresh')
    ) {
      original._retry = true;
      refreshPromise ??= refreshAccessToken().finally(() => {
        refreshPromise = null;
      });
      const newToken = await refreshPromise;
      if (newToken) {
        original.headers.Authorization = `Bearer ${newToken}`;
        return api.request(original);
      }
      // не получилось — на логин
      window.location.href = '/login';
      return Promise.reject(error);
    }

    // 403 з «No active membership» — tenant втратив активність.
    if (isMembershipLostError(error)) {
      // Скидаємо лише tenant — JWT валідний, user не повинен заново логінитись.
      setActiveTenant(null);
      // Full reload на /select-tenant: гарантує перечитування /my-tenants
      // і очищення React Query cache без явного доступу до queryClient.
      // Параметр reason=membership_lost дозволить сторінці показати тост.
      if (!window.location.pathname.startsWith('/select-tenant')) {
        window.location.href = '/select-tenant?reason=membership_lost';
      }
    }

    // 403 «Недостатньо прав» (Порція 3.0.7, UX γ-essential 2026-05-21) —
    // global UX feedback через toast. Більшість admin-дій уже сховано
    // через Sidebar + AutoRouteGuard + <RequirePermission> + UX-α/β
    // org-dropdown фільтр. Якщо user усе ж дійшов до 403 (наприклад
    // stale cache /auth/me після зміни прав adminом, або форма без
    // обгортки) — показуємо toast з detail з backend response. БЕЗ
    // перемикача org (поле залишається відкритим — user обирає правильну
    // org вручну і ретайнить).
    if (isInsufficientPermissionError(error)) {
      const detail = (error.response?.data as { detail?: string } | undefined)?.detail;
      showToastImperative({
        type: 'error',
        title: '⚠ Немає права на операцію',
        message: detail ?? 'Недостатньо прав для цієї дії.',
        durationMs: 8000, // довше за дефолт — щоб встигли прочитати detail
      });
    }

    // 409 Conflict (наприклад, UNIQUE constraint violation) — показуємо
    // detail з backend. Власник 2026-06-02: «duplicate key value violates
    // unique constraint uq_organizations_tenant_code» було 500 у БД,
    // переведено у 409 з людиночитаним повідомленням.
    if (error.response?.status === 409 && !isMutationSilenced(error)) {
      const detail = (error.response.data as { detail?: string } | undefined)?.detail;
      showToastImperative({
        type: 'error',
        title: '⚠ Конфлікт',
        message: detail ?? 'Запис з такими ключовими значеннями вже існує.',
        durationMs: 10000,
      });
    }

    // 500 Internal Server Error — показуємо короткий toast щоб клієнт не
    // думав «нічого не відбувається». Деталі — у docker logs.
    if (error.response?.status === 500 && !isMutationSilenced(error)) {
      showToastImperative({
        type: 'error',
        title: '⚠ Серверна помилка',
        message: 'Сервер не зміг обробити запит. Спробуйте ще раз або зверніться у підтримку.',
        durationMs: 10000,
      });
    }

    // 422 Pydantic ValidationError — показуємо детальне повідомлення.
    // FastAPI шле detail як list errors: [{loc, msg, type}, ...]
    // Власник 2026-06-02: «створюю Організацію — фоп на загальній — все
    // заповнив — не зберігає» — UI мовчки ковтав 422, тому що ця гілка
    // показувала тільки 403. Тепер ловимо обидва.
    if (error.response?.status === 422 && !isMutationSilenced(error)) {
      const data = error.response.data as
        { detail?: string | Array<{ loc?: unknown[]; msg?: string }> }
        | undefined;
      const detail = data?.detail;
      let message: string;
      if (typeof detail === 'string') {
        message = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        // Покажемо перші 3 помилки, читабельні.
        message = detail.slice(0, 3).map((e) => {
          const field = Array.isArray(e.loc) && e.loc.length > 1
            ? String(e.loc[e.loc.length - 1])
            : 'поле';
          return `${field}: ${e.msg ?? 'невідома помилка'}`;
        }).join('\n');
        if (detail.length > 3) message += `\n…і ще ${detail.length - 3} помилок`;
      } else {
        message = 'Невірні дані. Перевірте форму.';
      }
      showToastImperative({
        type: 'error',
        title: '⚠ Помилка валідації',
        message,
        durationMs: 12000,
      });
    }

    return Promise.reject(error);
  },
);
